import { useCallback, useEffect, useMemo } from 'react';

import { Row, Table } from '@tanstack/react-table';
import isEqual from 'lodash/isEqual';

import { SHOW_SELECTED_URL_PARAM } from '@/constants/constants';
import { useAppSelector } from '@/hooks/redux-hooks';
import { countOccurrence } from '@/pages/Catalog/CatalogTable/TableFilters/utils/countOccurrence';
import {
  alphabeticallySortedFilters,
  CountedLabelFunction,
  energyTransitionModules,
  labeledFilterColumns,
  oilOnlyModules
} from '@/pages/Catalog/constants';
import { useFilter } from '@/pages/Catalog/contexts/filter/useFilter';
import { CatalogItem } from '@/pages/Catalog/types';

type FilterOption = { label: string; count: number };

const useTableFilters = (table: Table<CatalogItem>) => {
  const { filters } = useFilter();
  const symbols = useAppSelector(state => state.symbolSelector.symbols);

  useEffect(() => {
    const { only, [SHOW_SELECTED_URL_PARAM]: showSelectedSymbols, search, ...rest } = filters;

    const codeColumn = table.getColumn('Code');
    if (codeColumn) {
      const current = (codeColumn.getFilterValue?.() as unknown[]) ?? [];
      const desired = showSelectedSymbols ? symbols : [];
      if (!isEqual(current, desired)) {
        codeColumn.setFilterValue(desired);
      }
    }

    for (const filter of Object.entries(rest)) {
      if (filter[0] === 'IndexModule' && only) {
        if (filter[1] === null) {
          if (filters.only === 'oil') {
            table.getColumn('IndexModule')?.setFilterValue(oilOnlyModules);
          } else if (filters.only === 'energyTransition') {
            table.getColumn('IndexModule')?.setFilterValue(energyTransitionModules);
          }
          continue;
        }
      }
      if (!isEqual(table.getColumn(filter[0])?.getFilterValue() ?? [], filter[1] ?? [])) {
        table.getColumn(filter[0])?.setFilterValue(filter[1] ? [filter[1]] : []);
      }
    }
  }, [filters, symbols]);

  const filteredRows: Array<Row<CatalogItem>> = table.getFilteredRowModel().rows;

  const getCountedLabels: CountedLabelFunction = useCallback(
    (key: string, joined?: boolean): FilterOption[] => {
      const counts: { [label: string]: number } = {};
      for (const row of filteredRows) {
        if (joined) {
          const label: string | undefined = row.getValue(key);
          const labels: string[] | undefined = label?.split(',');
          for (const l of labels ?? []) {
            countOccurrence(counts, l);
          }
        } else {
          const label: string | undefined = row.getValue(key);
          if (label) countOccurrence(counts, label);
        }
      }

      return Object.entries(counts)
        .map(([label, count]) => ({ label, count }))
        .sort((a, b) => {
          if (alphabeticallySortedFilters.includes(key)) {
            return a.label.localeCompare(b.label);
          }
          return b.count - a.count;
        });
    },
    [filteredRows]
  );

  const columnFiltersWithCounts = useMemo(
    () =>
      Object.entries(labeledFilterColumns).reduce(
        (acc, [key, value]) => ({
          ...acc,
          [key]: value(getCountedLabels)
        }),
        {} as Record<string, FilterOption[]>
      ),
    [getCountedLabels]
  );

  return {
    columnFiltersWithCounts
  };
};

export default useTableFilters;
